import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { DialogPortal, DialogOverlay } from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { CasillasAlta } from "@/components/legal/CasillasAlta";
import { Turnstile } from "@/components/auth/Turnstile";

const DNI_LETTERS = "TRWAGMYFPDXBNJZSQVHLCKE";
function validarNifCif(raw) {
  const s = String(raw || "").trim().toUpperCase().replace(/[-\s]/g, "");
  if (!s) return false;
  if (/^\d{8}[A-Z]$/.test(s)) return DNI_LETTERS[parseInt(s.slice(0, 8), 10) % 23] === s[8];
  if (/^[XYZ]\d{7}[A-Z]$/.test(s)) {
    const num = parseInt(String("XYZ".indexOf(s[0])) + s.slice(1, 8), 10);
    return DNI_LETTERS[num % 23] === s[8];
  }
  if (/^[ABCDEFGHJNPQRSUVW]\d{7}[0-9A-J]$/.test(s)) {
    let total = 0;
    for (let i = 0; i < 7; i++) {
      let d = parseInt(s[i + 1], 10);
      if (i % 2 === 0) { d *= 2; if (d > 9) d -= 9; }
      total += d;
    }
    const ctrl = (10 - (total % 10)) % 10;
    const letter = "JABCDEFGHI"[ctrl];
    const c = s[8];
    if ("PQSW".includes(s[0])) return c === letter;
    if ("ABEH".includes(s[0])) return c === String(ctrl);
    return c === String(ctrl) || c === letter;
  }
  return false;
}

function LoginForm() {
  const { user, login, register, forgotPassword, resetPassword } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [params] = useSearchParams();
  const nextParam = params.get("next") || "";
  const next = nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : "/dashboard";
  const enlaceReset = params.get("mode") === "reset" && params.get("email") && params.get("code");
  const [mode, setMode] = useState(enlaceReset ? "reset" : ["register", "forgot"].includes(params.get("mode")) ? params.get("mode") : "login"); // login | register | forgot | reset
  const [form, setForm] = useState({ name: "", email: enlaceReset ? params.get("email") : "", password: "", telefono: "", nif: "", direccion_fiscal: "", referido: params.get("ref") || localStorage.getItem("nv_ref") || "", acepta_profesional: false, acepta_legales: false, acepta_encargado: false, marketing: false });
  const [refOk, setRefOk] = useState(null);
  useEffect(() => {
    const c = (form.referido || "").trim();
    if (mode !== "register" || !c) { setRefOk(null); return undefined; }
    const t = setTimeout(() => {
      api.get("/referidos/validar", { params: { codigo: c } }).then((r) => setRefOk(r.data.valido)).catch(() => setRefOk(null));
    }, 350);
    return () => clearTimeout(t);
  }, [form.referido, mode]);
  const [reset, setReset] = useState({ code: enlaceReset ? params.get("code") : "", new_password: "" });
  const [loading, setLoading] = useState(false);
  const [verPw, setVerPw] = useState(false);
  const [captcha, setCaptcha] = useState("");
  const [captchaVez, setCaptchaVez] = useState(0);

  useEffect(() => {
    if (user) navigate(next);
  }, [user, navigate, next]);
  useEffect(() => {
    if (enlaceReset) window.history.replaceState(null, "", "/login");
  }, [enlaceReset]);


  const pw = form.password;
  const pwChecks = { len: pw.length >= 8, upper: /[A-Z]/.test(pw), lower: /[a-z]/.test(pw), digit: /\d/.test(pw) };
  const pwScore = Object.values(pwChecks).filter(Boolean).length;
  const pwStrong = pwChecks.len && pwChecks.upper && pwChecks.lower && pwChecks.digit;
  const pwLabel = ["Muy débil", "Débil", "Aceptable", "Buena", "Fuerte"][pwScore];
  const pwColor = ["bg-slate-200", "bg-rose-500", "bg-amber-500", "bg-lime-500", "bg-emerald-500"][pwScore];
  const nifValid = validarNifCif(form.nif);

  const rp = reset.new_password;
  const rpStrong = rp.length >= 8 && /[A-Z]/.test(rp) && /[a-z]/.test(rp) && /\d/.test(rp);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !pwStrong) {
      toast.error("La contraseña debe tener mínimo 8 caracteres, con mayúscula, minúscula y número");
      return;
    }
    if (mode === "register" && !nifValid) {
      toast.error("El NIF/CIF no es válido. Revisa el número y la letra de control.");
      return;
    }
    if (mode === "register" && !(form.acepta_profesional && form.acepta_legales && form.acepta_encargado)) {
      toast.error("Marca las tres casillas obligatorias para crear tu cuenta.");
      return;
    }
    if (mode === "register" && !captcha) {
      toast.error("Completa la comprobación antirrobots antes de crear la cuenta.");
      return;
    }
    if (mode === "reset" && !rpStrong) {
      toast.error("La nueva contraseña debe tener mínimo 8 caracteres, con mayúscula, minúscula y número");
      return;
    }
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Bienvenido a NuvliPro");
        navigate(next);
      } else if (mode === "register") {
        await register({ ...form, turnstile_token: captcha, idioma: (i18n.language || "es").split("-")[0] });
        localStorage.removeItem("nv_ref");
        toast.success("Bienvenido a NuvliPro");
        navigate(next);
      } else if (mode === "forgot") {
        await forgotPassword(form.email);
        toast.success("Si el email existe, te hemos enviado un código de recuperación");
        setMode("reset");
      } else if (mode === "reset") {
        await resetPassword(form.email, reset.code, reset.new_password);
        toast.success("Contraseña actualizada. Ya puedes iniciar sesión.");
        setReset({ code: "", new_password: "" });
        setForm({ ...form, password: "" });
        setMode("login");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      if (mode === "register") setCaptchaVez((v) => v + 1);
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setR = (k) => (e) => setReset({ ...reset, [k]: e.target.value });

  const titles = {
    login: ["Iniciar sesión", "Accede a tu panel profesional"],
    register: ["Crear cuenta", "Empieza gratis con el Plan Gratuito, sin tarjeta"],
    forgot: ["Recuperar contraseña", "Introduce el correo de suscripción con el que te registraste (tu usuario) y te enviaremos un código."],
    reset: ["Restablecer contraseña", "Introduce el código recibido y tu nueva contraseña"],
  };

  return (
    <div className="p-6 sm:p-8" data-testid="login-modal-body">
            <div className="flex items-center gap-2.5 mb-6">
              <div className="w-10 h-10 rounded-xl bg-white border-2 border-[#0A2540] flex items-center justify-center overflow-hidden">
                <img src="/nuvlipro-logo.png" alt="NuvliPro" className="w-full h-full object-contain p-1" />
              </div>
              <span className="font-heading font-extrabold text-xl text-[#0A2540]">Nuvli<span className="text-emerald-600">Pro</span></span>
            </div>
            {(mode === "forgot" || mode === "reset") && (
              <button onClick={() => setMode("login")} data-testid="back-to-login" className="flex items-center gap-1 text-sm text-[#0284C7] hover:underline mb-3">
                <ArrowLeft className="w-4 h-4" /> Volver a iniciar sesión
              </button>
            )}
            <DialogPrimitive.Title className="font-heading text-2xl font-extrabold text-[#0A2540] mb-1" data-testid="login-modal-title">{titles[mode][0]}</DialogPrimitive.Title>
            <DialogPrimitive.Description className="text-sm text-slate-600 mb-6">{titles[mode][1]}</DialogPrimitive.Description>
            <form onSubmit={submit} className="space-y-4 [&_input]:bg-white [&_input]:rounded-xl [&_input]:border-[#0A2540]/25 [&_input]:h-11">
              {mode === "register" && (
                <div>
                  <Label htmlFor="name">Nombre completo</Label>
                  <Input id="name" data-testid="register-name" value={form.name} onChange={set("name")} required className="mt-1.5" placeholder="Tu nombre o razón social" />
                </div>
              )}
              {mode !== "reset" && (
                <div>
                  <Label htmlFor="email">{mode === "forgot" ? "Correo de suscripción (usuario)" : "Email"}</Label>
                  <Input id="email" type="email" data-testid="login-email" value={form.email} onChange={set("email")} required className="mt-1.5" placeholder="tu@email.com" />
                  {mode === "forgot" && (
                    <p className="text-xs text-slate-500 mt-1.5" data-testid="forgot-hint">
                      Usa el mismo correo con el que iniciaste tu suscripción como profesional. Es tu nombre de usuario.
                    </p>
                  )}
                </div>
              )}
              {(mode === "login" || mode === "register") && (
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Contraseña</Label>
                    {mode === "login" && (
                      <button type="button" onClick={() => setMode("forgot")} data-testid="forgot-password-link" className="text-xs text-[#0284C7] font-medium hover:underline">
                        ¿Olvidaste tu contraseña?
                      </button>
                    )}
                  </div>
                  <div className="relative mt-1.5">
                    <Input id="password" type={verPw ? "text" : "password"} data-testid="login-password" value={form.password} onChange={set("password")} required className="pr-10" placeholder="••••••••" />
                    <button type="button" onClick={() => setVerPw((v) => !v)} aria-label={verPw ? "Ocultar contraseña" : "Mostrar contraseña"} aria-pressed={verPw}
                      className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md text-[#00B4CC] transition-opacity hover:opacity-80" data-testid="login-password-toggle">
                      {verPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {mode === "register" && pw.length > 0 && (
                    <div className="mt-2" data-testid="password-strength">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= pwScore ? pwColor : "bg-slate-200"}`} />
                        ))}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Fortaleza: <span className="font-medium">{pwLabel}</span> · mín. 8 caracteres con mayúscula, minúscula y número.
                      </p>
                    </div>
                  )}
                </div>
              )}
              {mode === "reset" && (
                <>
                  <div>
                    <Label htmlFor="reset-email">Email</Label>
                    <Input id="reset-email" type="email" data-testid="reset-email" value={form.email} onChange={set("email")} required className="mt-1.5" placeholder="tu@email.com" />
                  </div>
                  <div>
                    <Label htmlFor="reset-code">Código de recuperación</Label>
                    <Input id="reset-code" data-testid="reset-code" value={reset.code} onChange={setR("code")} required className="mt-1.5 tracking-widest" placeholder="000000" maxLength={6} />
                  </div>
                  <div>
                    <Label htmlFor="reset-pwd">Nueva contraseña</Label>
                    <Input id="reset-pwd" type="password" data-testid="reset-new-password" value={reset.new_password} onChange={setR("new_password")} required className="mt-1.5" placeholder="••••••••" />
                    <p className="text-xs text-slate-400 mt-1">Mín. 8 caracteres con mayúscula, minúscula y número.</p>
                  </div>
                </>
              )}
              {mode === "register" && (
                <>
                  <div>
                    <Label htmlFor="telefono">Teléfono</Label>
                    <Input id="telefono" data-testid="register-telefono" value={form.telefono} onChange={set("telefono")} required className="mt-1.5" placeholder="+34600111222" />
                    <p className="text-xs text-slate-400 mt-1">Formato internacional (E.164), p. ej. +34600111222.</p>
                  </div>
                  <div>
                    <Label htmlFor="nif">NIF / CIF</Label>
                    <Input id="nif" data-testid="register-nif" value={form.nif} onChange={set("nif")} required className={`mt-1.5 ${form.nif && !nifValid ? "border-rose-400 focus-visible:ring-rose-400" : ""}`} placeholder="B12345678 / 12345678Z" />
                    {form.nif.length > 0 && (
                      <p className={`text-xs mt-1 ${nifValid ? "text-emerald-600" : "text-rose-500"}`} data-testid="nif-hint">
                        {nifValid ? "NIF/CIF válido ✓" : "NIF/CIF no válido: revisa el número y la letra de control."}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="direccion_fiscal">Dirección fiscal</Label>
                    <Input id="direccion_fiscal" data-testid="register-direccion-fiscal" value={form.direccion_fiscal} onChange={set("direccion_fiscal")} required className="mt-1.5" placeholder="Calle, número, CP, población" />
                  </div>
                  <div>
                    <Label htmlFor="referido">Código de invitación (opcional)</Label>
                    <Input id="referido" data-testid="register-referido" value={form.referido} onChange={set("referido")} maxLength={24} className="mt-1.5 uppercase" placeholder="Ej. K7M2QX9P" />
                    {refOk !== null && (
                      <p data-testid="register-referido-hint" className={`text-xs mt-1 ${refOk ? "text-emerald-600" : "text-amber-600"}`}>
                        {refOk ? "Código válido: tendrás un 10 % de descuento en tu primer pago." : "Este código no es válido. Puedes dejarlo vacío."}
                      </p>
                    )}
                    <a href="/legal/referidos" target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-xs text-[#0284C7] hover:underline" data-testid="register-referido-condiciones">Condiciones del programa de referidos</a>
                  </div>
                  <CasillasAlta valores={form} onChange={setForm} />
                  <Turnstile onToken={setCaptcha} reinicio={captchaVez} />
                </>
              )}
              <Button type="submit" disabled={loading} data-testid="submit-auth" className="w-full rounded-xl bg-[#0A2540] hover:bg-[#031B33] text-white h-11 font-semibold transition-colors">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {mode === "login" ? "Entrar" : mode === "register" ? "Crear cuenta" : mode === "forgot" ? "Enviar código" : "Restablecer contraseña"}
              </Button>
            </form>
            {(mode === "login" || mode === "register") && (
              <p className="text-sm text-center text-slate-500 mt-6">
                {mode === "login" ? "¿No tienes cuenta?" : "¿Ya tienes cuenta?"}{" "}
                <button
                  onClick={() => setMode(mode === "login" ? "register" : "login")}
                  data-testid="toggle-auth-mode"
                  className="text-[#0284C7] font-semibold hover:underline"
                >
                  {mode === "login" ? "Regístrate" : "Inicia sesión"}
                </button>
              </p>
            )}
            {mode === "forgot" && (
              <p className="text-sm text-center text-slate-500 mt-6">
                ¿Ya tienes el código?{" "}
                <button onClick={() => setMode("reset")} data-testid="have-code-link" className="text-[#0284C7] font-semibold hover:underline">
                  Introducirlo aquí
                </button>
              </p>
            )}
    </div>
  );
}

export default function LoginDialog({ open, onClose }) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0A2540]/70 backdrop-blur-sm" data-testid="login-modal-overlay" />
        <DialogPrimitive.Content data-testid="login-modal"
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-1.5rem)] max-w-md -translate-x-1/2 -translate-y-1/2 max-h-[92vh] overflow-y-auto rounded-3xl border-2 border-[#0A2540] bg-gradient-to-b from-[#E6FAF2] to-[#CDF5E4] shadow-2xl shadow-[#0A2540]/30 duration-200 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95">
          <DialogPrimitive.Close className="absolute right-4 top-4 w-8 h-8 rounded-full border border-[#0A2540]/20 bg-white/70 flex items-center justify-center text-[#0A2540] hover:bg-white transition-colors" data-testid="login-modal-close">
            <X className="w-4 h-4" /><span className="sr-only">Cerrar</span>
          </DialogPrimitive.Close>
          <LoginForm />
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogPrimitive.Root>
  );
}
